/*!
 *  (c)(r) 2008-2023 IT-Service Robert Frunzke
 */

import { arr_inArray } from './util.js';

/**
 *
 * MPlugins - manage plugins of different kinds in manja UI
 *
 *
 * basic usage:
 * 	- register a new plugin using MPlugins.register($,mj,'some-name',plugin);
 *  - registration should occur directly on page load, even before document ready event
 *  - later (after document ready and setup of fundamental stuff), the 'initPlugin()' method
 *    is called on all plugins (which actually have such a method)
 *
 *
 * plugin objects:
 *  - simple objects, usually in "literal notation" (thought this could change, they create unnecessarily heavy load on the interpreter)
 *  - contain at least the 'type' property
 *  - contain plugin-specific members and methods
 *  - members and methods are specified by convention only (it all depends on the actual code that calls into the plugin),
 *  - instead of an object, register() will also accept a factory function($,mj), which must create and return the plugin object
 *
 * example:
 *
 * 		MPlugins.register( [], 'example-fun', {
 * 			type: 'MediaMetaViewData',
 * 			meta_ids: [ 101 ],
 * 			preSetupViewData: function( media_id, media_detail_info, mdd ) {
 * 			},
 * 			postSetupViewData: function( media_id, media_detail_info, mdd ) {
 * 			},
 * 			setupViewData: function( media_id, meta_id, tr, val_arr, val_html, media_detail_info, plugin_context ) {
 * 				const td = tr.children('td');
 * 				let dh = td.children('.data-holder');
 * 				if( !dh.length ) td.html(dh=MVMediaMetadata.wrapWithMetaDataHolder(meta_id,val_html+'&nbsp;'));
 * 				dh.append('<span class="xinfo">I am a rather useless comment below the title.&nbsp;</span>');
 * 				tr.removeClass('md-hide').addClass('md-avl');
 * 			}
 * 		} );
 *
 *
 * example using the factory function:
 * - incidentally, a local anonymous namespace emerges, which should be used by the plugin for its own state.
 *
 * 		MPlugins.register( [$,mj], 'example-fun', function($,mj) {
 *
 *			let some_data_in_anon_namespace_visible_for_plugin_only = 123;
 *
 *			return {
 * 				type: 'MediaMetaViewData',
 * 				meta_ids: [ 101 ],
 * 				preSetupViewData: function( media_id, media_detail_info, mdd ) {
 * 				},
 * 				postSetupViewData: function( media_id, media_detail_info, mdd ) {
 * 				},
 * 				setupViewData: function( media_id, meta_id, tr, val_arr, val_html, media_detail_info, plugin_context ) {
 * 					const td = tr.children('td');
 * 					let dh = td.children('.data-holder');
 * 					if( !dh.length ) td.html(dh=MVMediaMetadata.wrapWithMetaDataHolder(meta_id,val_html+'&nbsp;'));
 * 					dh.append('<span class="xinfo">I am a rather useless comment below the title.&nbsp;</span>');
 * 					tr.removeClass('md-hide').addClass('md-avl');
 * 				}
 * 			};
 * 		} );
 *
 *
 * plugin types, their data and method signatures:
 *
 *
 * 		type:							'MediaMetaDisplayData'
 * 		meta_ids:						array of meta ids
 * 		prepareDisplayData				function( meta_def, data_val )
 * 			- optional
 * 			- called in MVMediaMetadata.getInteractiveDisplayDataItem
 * 			- should return object with a function getDisplayValueFragment( search_val, disp_val, data_val_offs, context, plain_text, make_link )
 *
 * 		getDisplayDataItem				function( meta_def, data_val, plain_text, make_link )
 * 			- optional
 * 			- called in MDMetaRenderer.getDisplayDataItem()
 *
 *
 *
 * 		type:							'MediaMetaViewData'
 * 		meta_ids:						array of meta ids
 * 		preSetupViewData				function( media_id, media_detail_info, mdd )
 * 			- optional
 * 			- called on plugins before view setup
 * 			- may return an arbitrary context object that will be passed to setupViewData() and postSetupViewData() calls
 * 		postSetupViewData				function( media_id, media_detail_info, mdd, context )
 * 			- optional
 * 			- called on plugins after view setup
 * 		setupViewData:					function( media_id, meta_id, tr, val_arr, val_html, dtinfo, context )
 * 			- called for each row in view, matching one of the meta_ids
 * 			- the view of the row is already set up according to standard rules
 *
 *
 *
 * 		type:							'MediaMetaEditView'
 * 		meta_ids:						array of meta ids
 * 		setupEditView:					function( media_id, meta_id, btns_div, wdgs_div, val_arr, dtinfo, close_view, update_canvas )
 *
 *
 *
 * 		type:							'MediaMetaSaveData'
 * 		meta_ids:						array of meta ids
 * 		beforeSaveMetaData:	function( media_id, meta_id, data, cur_data )
 * 			- called (in a preprocessing step) before saving meta data
 * 			- may alter the data (even other fields or categories)
 * 			- data is an object { meta: { meta_id:[v,v,..], ... }, categories: { tree_id:[c,c,..], ... } }
 * 			- cur_dtinfos contains the current (onmodified) data,
 *
 *
 *
 * 		type:							'MediaMetaViewContainer'
 *		getAdditionalContainerCssClasses:	(optional) function() -> returns string
 *		getContainerDefaultOpened:			(optional) function() -> returns bool
 * 		getContainerTitle:					function() -> returns string
 * 			getViewSetupRows:				function() -> returns array of html, each row must consist of a '<tr>..</tr>'
 * 				- called on view setup (when no data yet available)
 * 			setupRowsData:					function( container, rows, media_id, dtinfo )
 * 				- called on view data setup (when actual metadata is available)
 * 		OR ALTERNATIVELY:
 * 			getContainerViewSetupHtml:		function() -> returns html string,
 * 				- called on view setup (when no data yet available)
 * 			setupContainerViewData:			function( container, media_id, dtinfo )
 * 				- called on view data setup (when actual metadata is available)
 *
 *
 *
 *  	type:							'MediaPreview'
 *  	canCreatePreview:				function( dtinfo )
 *  		- called before view setup
 * 			- must return true if this plugin can create a preview for provided item
 *			- dtinfo must contain that information in appropriate meta data (dtinfo.media_class or dtinfo.meta[-1][0]).
 *
 *  	createPreview:					function( dtinfo, target, xopts )
 *  		- called on view setup - same as in js/media.*.js: createPreview()
 *  		- target will receive these events:
 *  			'mjstoolbarsetup'				md_toolbar0 (dom elem)
 *  			'mjsclose'						-
 *  			'mjsresize'						w, h
 *  			'mjsshow'						-
 *  			'mjsmenuhover'					which (string), menu (jquery), update_menu_cb (callback)
 *  			'mjsoverlaysremove'				-
 *  			'mjsoverlayssetup'				media_bbox, ref_bbox, overlays
 *  			'mjszoomsyncset'				zoom_stack, from_w, from_h, ...
 *  			'mjspageset'					page (int)
 *
 *  		- may trigger events on target:
 *  			'mjsbeforepagechanged'			page (int)
 *  			'mjsafterpagechanged'			page (int)
 *  			'mjsafterzoomto'				stack, w, h, ...
 *  			'mjsafterzoomchanged'			-
 *  			'mjsbeforeclippathchanged'		clip_path (int)
 *  			'mjsafterclippathchanged'		clip_path (int)
 *  			'mjsbeforeboxchanged'			box (string)
 *  			'mjsafterboxchanged'			box (string)
 *
 *  		- may trigger events on document:
 *  			'mjsrecreatethumbnails'			media_id (int), meta
 *
 *
 *
 *  	type:							'MediaThumbnailView'
 *  	meta_ids:						array of meta_ids - required for this add-ons functionality
 *  	bindStatusChanged:				function( view_mode, cb )
 *  	queryMediaViewStatus:			function( view_mode, media_ids, meta, trigger_status_change )
 *  	resetViewNode:					function( view_mode, node, media_id )
 *  	updateViewNode:					function( view_mode, node, media_id, dtinfo )
 *  		- view_mode is one of: 'lightbox', 'result', 'mj-thumb-box', 'link'
 *  		- this is performance critical, so this should usually just toggle one or a few
 *  		  css classes on the node (e.g. using toggleMultipleClasses() helper function)
 *  	initViewNodesRoot:				function( view_mode, root, on_action )
 *  		- here, event handlers can be bound to root node of view nodes
 *  		- on_action is function( event, action )
 *
 *
 *
 *  	type:							'MediaSendView'
 * 		setupSendSelectView:			function( list_item, $link_parent_elem, on_click )
 * 		handleSendInfoResponse:			function( j, pluginContext )
 * 		preSetupSendViewData:			function( $list_elem, pluginContext )
 *		getMailSubjectText:				function( sender, rcpt, pluginContext )
 *		postProcessAvailableRcptsList	function( sender, rcpt_list, pluginContext )
 * 		setupSendView:					function( $container_elem, $context_elem, sender, pluginContext )
 * 		beforeSubmit:					function( $container_elem, $form_fields_1, plugins_extra_postdata, pluginContext )
 * 		afterSubmit:					function( response_data, pluginContext )
 *
 *
 *
 * 		type:							'MediaLightboxView'
 * 		getOptGroupLabel: 				function( group_type, resultObj )
 * 		createItemPlugin: 				function( group_type, voting_id, lb_object_data, on_plugin_ctx_create )
 * 			- returns instance of an object with methods:
 *					beforeDetachFromLightbox()
 *					toggleLightboxActions()
 *					afterAttachToLightbox( mhl_elem )
 *					beforeUpdateSmallView( mv_open, mls_elem )
 *
 *
 *
 * 		type:							'MediaResultView'
 * 		beforeUpdateResult:				function( result_elem, $ctx_toolbar, list_type, object_id, object_item )
 * 		selectionChanged:				function( sel_actually_changed, sel_media_ids )
 *
 *
 *
 * 		type:							'MediaResultInfoView'
 * 		afterUpdateResultInfo:			function( info_elem, list_data_obj, search_info_obj )
 *
 *
 */
export let MPlugins = function(window) {

	let m_plugins = {};				// { name:plugin, ... }

	let m_plugins_by_type = {
		MediaMetaViewData: {},
		MediaMetaEditView: {},
		MediaMetaSaveData: {},
		MediaMetaViewContainer: {},
		MediaPreview: {},
		MediaThumbnailView: {},
	};

	/**
	 * Register a plugin.
	 *
	 * @param {Array} args
	 * @param {string} name - The plugin name.
	 * @param {Object} plugin - The plugin implementation.
	 */
	function register( args, name, plugin ) {
		if( m_plugins.hasOwnProperty(name) ) {
			if( window.console && window.console.log ) window.console.log('MPlugins: attempt to register plugin twice');
			return;
		}
		if( typeof plugin === 'function' ) plugin = plugin.apply(window,args);
		plugin.name = name;
		m_plugins[name] = plugin;
		if( !m_plugins_by_type.hasOwnProperty(plugin.type) ) m_plugins_by_type[plugin.type] = {};
		m_plugins_by_type[plugin.type][name] = plugin;
	}

	/**
	 * Determine whether a plugin is registered.
	 * @param {string} name - The plugin name.
	 * @returns {boolean}
	 */
	function isRegistered( name ) {
		return m_plugins.hasOwnProperty(name);
	}

	/**
	 * Initialize all registered plugins.
	 */
	function init() {
		for( const plugin of Object.values(m_plugins) ) {
			if( plugin.hasOwnProperty('initPlugin') ) plugin.initPlugin();
		}
	}



	/**
	 * @callback PluginIterationCallback
	 * @param {string} name - The plugin name.
	 * @param {Object} plugin - The plugin implementation.
	 */


	/**
	 * Iterate on plugins of a given type.
	 * @param {string} type - The plugin type.
	 * @param {PluginIterationCallback} cb
	 */
	function iterateByType( type, cb ) {
		if( !m_plugins_by_type.hasOwnProperty(type) ) return;
		for( const [name,plugin] of Object.entries(m_plugins_by_type[type]) ) {
			const r = cb(name,plugin);
			if( r===false ) break; // early exit loop
		}
	}

	/**
	 * Get array of plugins of a given type.
	 * @param {string} type - The plugin type.
	 * @returns {Object[]}
	 */
	function getByType( type ) {
		let plugins_arr = [];
		if( m_plugins_by_type.hasOwnProperty(type) ) {
			let plugins_by_name = m_plugins_by_type[type];
			for( const k in plugins_by_name ) {
				if( plugins_by_name.hasOwnProperty(k) ) plugins_arr.push(plugins_by_name[k]);
			}
		}
		return plugins_arr;
	}


	/**
	 * Helper class which dispatches a specific function call to multiple implementations.
	 */
	class MultiplePluginsDelegate {
		/**
		 * @param {string} proxy_method_name
		 * @param {Object} plugin_a
		 * @param {Object} plugin_b
		 */
		constructor( proxy_method_name, plugin_a, plugin_b ) {
			this.plugins = [ plugin_a, plugin_b ];
			this[proxy_method_name] = function() {
				for( var i=0; i<this.plugins.length; ++i ) this.plugins[i][proxy_method_name].apply(this,arguments);
			};
		}
		/**
		 *
		 * @param {Object} plugin
		 */
		push( plugin ) {
			this.plugins.push(plugin);
		}
	}


	/**
	 * NOTE: if multiple plugins registered on the same meta_id, then this will construct a proxy
	 *  object which will delegate calls to all appropriate plugins!
	 * For performance reasons this will support proxying of a single method only - specified by name.
	 *
	 * @param {string} type - The plugin type.
	 * @param {string} proxy_method_name - The method which will be called on plugin later on.
	 * @returns {Object}
	 */
	function getByTypeMappedByMetaId( type, proxy_method_name ) {
		let plugins_by_meta_id = {};
		if( !m_plugins_by_type.hasOwnProperty(type) ) return plugins_by_meta_id;

		for( const plugin of Object.values(m_plugins_by_type[type]) ) {
			for( let i=0, l=plugin.meta_ids.length; i<l; ++i ) {
				const meta_id = plugin.meta_ids[i];
				if( !plugins_by_meta_id.hasOwnProperty(meta_id) ) {
					// set plain plugin reference ..
					plugins_by_meta_id[meta_id] = plugin;
					continue;
				}
				// multiple plugins are responsible for this meta_id .. -> create or extend a delegate ..
				const wrapped = plugins_by_meta_id[meta_id];
				if( wrapped instanceof MultiplePluginsDelegate ) {
					// wrapper already exists -> add to it
					wrapped.push(plugin);
				} else {
					// create wrapper ...
					plugins_by_meta_id[meta_id] = new MultiplePluginsDelegate(proxy_method_name,wrapped,plugin);
				}
			}
		}
		return plugins_by_meta_id;
	}

	/**
	 * Iterate on plugins of a given type and meta_id.
	 *
	 * @param {string} type - The plugin type.
	 * @param {string|number} meta_id
	 * @param {PluginIterationCallback} cb
	 */
	function iterateByTypeAndMetaId( type, meta_id, cb ) {
		if( !m_plugins_by_type.hasOwnProperty(type) ) return;
		meta_id = parseInt(meta_id,10);
		for( const [name,plugin] of Object.entries(m_plugins_by_type[type]) ) {
			if( arr_inArray(meta_id,plugin.meta_ids) !== -1 ) {
				const r = cb(name,plugin);
				if( r===false ) break; // early exit loop
			}
		}
	}

	/**
	 * Determine whether a plugin of type for meta_id was registered.
	 * @param {string} type - The plugin type.
	 * @param {string|number} meta_id
	 * @returns {boolean}
	 */
	function hasPluginOfTypeForMetaId( type, meta_id ) {
		if( !m_plugins_by_type.hasOwnProperty(type) ) return false;
		meta_id = parseInt(meta_id,10);
		for( const plugin of Object.values(m_plugins_by_type[type]) ) {
			if( arr_inArray(meta_id,plugin.meta_ids) !== -1 ) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Get plugin implementation by name.
	 * @param {string} name - The plugin name.
	 * @returns {?Object} - The plugin implementation.
	 */
	function getByName( name ) {
		return m_plugins.hasOwnProperty(name) ? m_plugins[name] : null;
	}

	/**
	 * Get plugin implementation by type name.
	 * @param {string} type - The plugin type.
	 * @param {string} name - The plugin name.
	 * @returns {?Object} - The plugin implementation.
	 */
	function getByTypeAndName( type, name ) {
		return m_plugins_by_type.hasOwnProperty(type) && m_plugins_by_type[type].hasOwnProperty(name) ? m_plugins_by_type[type][name] : null;
	}


	/**
	 * Call a function on all plugins of a type.
	 *
	 * @param {string} type - The plugin type.
	 * @param {string} fn - The function name.
	 * @param {boolean} fn_required - Whether the function is required to exist on plugin.
	 * @param args
	 */
	function callOnAllPluginsOfType( type, fn, fn_required, ...args ) {
		if( !m_plugins_by_type.hasOwnProperty(type) ) return;
		for( const [name,plugin] of Object.entries(m_plugins_by_type[type]) ) {
			if( !plugin.hasOwnProperty(fn) ) {
				if( fn_required ) throw new Error('plugin '+name+' does not implement required function '+fn);
				continue; // skip this plugin
			}
			plugin[fn].apply(plugin,args);
		}
	}


	return {
		register,
		isRegistered,
		init,
		iterateByType,

		getByType,

		getByTypeMappedByMetaId,
		iterateByTypeAndMetaId,
		hasPluginOfTypeForMetaId,

		getByName,
		getByTypeAndName,

		callOnAllPluginsOfType,
	};

}(window); // namespace MPlugins
